"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { BanknoteCheck, Landmark, ListCheck, PencilLine } from "lucide-react";
import { signIn, signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import {
	Alert,
	Button,
	Card,
	CardContent,
	List,
	Stack,
	Text,
} from "@leabankab/component-library";
import {
	clearGa4LoginStarted,
	markGa4LoginStarted,
	trackGa4Event,
} from "@/analytics/ga4";
import { useToast } from "@/components/feedback";
import { StepContainer } from "@/components/layout";
import {
	SigningFailed,
	SigningSessionExpired,
	WrongUser,
} from "@/components/organisms/signing-error";
import { Heading } from "@/components/ui";
import { BankidSe } from "@/components/ui/svg/bankid-se";
import {
	getSigningProviderId,
	getSilentAuthParams,
	getSimulatorAuthParams,
} from "@/config/auth-providers";
import { useCountry } from "@/config/country-context";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import {
	AUTH_ROUND_TRIP_MAX_AGE_SECONDS,
	SILENT_AUTH_FLOW_ID_COOKIE,
} from "@/core/auth/flow-id-from-request";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import {
	LOGIN_STATUS_FAILED,
	LOGIN_STATUS_QUERY,
	LOGIN_STATUS_SESSION_EXPIRED,
	LOGIN_STATUS_TIMEOUT,
	LOGIN_STATUS_WRONG_USER,
} from "@/features/login/login-status";
import {
	buildPublicUrl,
	getPublicOrigin,
} from "@/features/login/public-origin";
import {
	SILENT_AUTH_ATTEMPT_COOKIE,
	SILENT_AUTH_ERROR_COOKIE,
	buildSilentAuthSuccessPath,
	buildSilentAuthUnavailablePath,
	shouldAttemptSilentAuth,
} from "@/features/login/silent-auth";

const ERROR_CODE_QUERY = "errorCode";

interface SignInOptions {
	useSimulator?: boolean;
	/** Ask Keycloak to re-authenticate rather than reuse an existing session. */
	forceReauth?: boolean;
}
const SILENT_AUTH_RETURN_COOKIE = "lea_silent_auth_return";

const SIMULATOR_BUTTON_LABEL = "Log in with Simulator (test only)";

interface LoginPageProps {
	flowId: string;
	fromApplication?: boolean;
	silentAuthAttempted?: boolean;
	silentAuthErrorCode?: string;
}

export const LoginPage = ({
	flowId,
	fromApplication = false,
	silentAuthAttempted = false,
	silentAuthErrorCode,
}: LoginPageProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const country = useCountry();

	const status = searchParams.get(LOGIN_STATUS_QUERY);
	const errorCode = searchParams.get(ERROR_CODE_QUERY) ?? silentAuthErrorCode;
	const authError = searchParams.get("error");
	const hasStartedSilentAuth = useRef(false);
	const hasClearedWrongUser = useRef(false);
	const loggedErrorSignature = useRef<string | undefined>(undefined);
	// Auth.js keeps one state/PKCE cookie pair per browser, so a second
	// authorize request overwrites the first and both callbacks then fail the
	// state check. A ref, not the state below, because two taps in the same
	// tick would both read a stale render.
	const isSignInInFlight = useRef(false);
	const [isSigningIn, setIsSigningIn] = useState(false);

	const showWrongUser = status === LOGIN_STATUS_WRONG_USER;
	const showSigningFailed =
		!showWrongUser && (status === LOGIN_STATUS_FAILED || Boolean(authError));
	const showSessionExpired =
		!showWrongUser && !showSigningFailed && status === LOGIN_STATUS_TIMEOUT;

	const t = useTranslations("pages.LoginPage");

	const { simulatorLogin: showSimulatorLogin, publicUrl } = useRuntimeConfig();

	const buildLoginCallbackUrl = useCallback(
		(search?: string) => {
			const callbackUrl = new URL(
				`/${flowId}/login`,
				getPublicOrigin(publicUrl)
			);
			if (search) {
				callbackUrl.search = search;
			}
			return callbackUrl.toString();
		},
		[flowId, publicUrl]
	);

	const { showToast } = useToast();

	const startSignIn = useCallback(
		async ({
			useSimulator = false,
			forceReauth = false,
		}: SignInOptions = {}) => {
			const callbackUrl = buildLoginCallbackUrl();
			const publicOrigin = getPublicOrigin(publicUrl);
			const provider = getSigningProviderId(country);

			if (isSignInInFlight.current) {
				sendClientLog({
					level: "info",
					event: LogEvent.AuthLoginDuplicateSuppressed,
					message: "Ignored a sign-in start while one was already in flight",
					flowId,
					country,
					context: { provider, useSimulator, forceReauth },
				});
				return;
			}
			isSignInInFlight.current = true;
			setIsSigningIn(true);
			markGa4LoginStarted();
			trackGa4Event("signing_login_started");

			const authParams = {
				...(useSimulator ? getSimulatorAuthParams() : undefined),
				// Keycloak would otherwise reuse the session the customer needs to
				// get out of.
				...(forceReauth ? { prompt: "login" } : undefined),
			};

			sendClientLog({
				level: "info",
				event: LogEvent.AuthLoginStarted,
				message: "Signing sign-in started",
				flowId,
				country,
				context: {
					useSimulator,
					provider,
					signInUrl: `${publicOrigin}/api/auth/signin/${provider}`,
					callbackUrl,
					authParams,
				},
			});
			try {
				await signIn(
					provider,
					{ callbackUrl },
					Object.keys(authParams).length > 0 ? authParams : undefined
				);
			} catch (error) {
				// A successful signIn navigates away, so the guard is only released
				// here: the customer is still on the page and needs the retry.
				isSignInInFlight.current = false;
				setIsSigningIn(false);
				clearGa4LoginStarted();
				trackGa4Event("signing_login_failed", {
					error_category: "authentication",
				});
				showToast({ severity: "error", message: t("signInFailed") });

				sendClientLog({
					level: "error",
					event: LogEvent.AuthLoginFailed,
					outcome: "failure",
					message: "Signing sign-in failed",
					flowId,
					country,
					context: { provider, callbackUrl },
					error: error as Error,
				});
			}
		},
		[buildLoginCallbackUrl, flowId, country, showToast, t, publicUrl]
	);

	// The wrong person's session would otherwise send the resolver straight back
	// here on every navigation, so it is dropped before the retry is offered.
	useEffect(() => {
		if (!showWrongUser || hasClearedWrongUser.current) return;
		hasClearedWrongUser.current = true;

		void signOut({ redirect: false }).catch((error) => {
			sendClientLog({
				level: "warn",
				event: LogEvent.AuthWrongUser,
				outcome: "failure",
				message: "Unable to clear the session of a non-applicant",
				flowId,
				country,
				error: error as Error,
			});
		});
	}, [country, flowId, showWrongUser]);

	useEffect(() => {
		if (!showSigningFailed && !showSessionExpired && !showWrongUser) return;

		const signature = `${status}|${authError}|${errorCode}`;
		if (loggedErrorSignature.current === signature) return;
		loggedErrorSignature.current = signature;
		clearGa4LoginStarted();
		if (showSessionExpired) {
			trackGa4Event("signing_session_timeout", {
				error_category: "timeout",
			});
		} else {
			trackGa4Event("signing_login_failed", {
				error_category: "authentication",
			});
		}

		sendClientLog({
			level: "error",
			event: showWrongUser ? LogEvent.AuthWrongUser : LogEvent.AuthLoginFailed,
			outcome: "failure",
			message: showWrongUser
				? "Login screen asked the customer to retry with the applicant's BankID"
				: showSessionExpired
					? "Signing session expired before authentication completed"
					: "Login screen reached an authentication failure state",
			flowId,
			country,
			context: {
				provider: getSigningProviderId(country),
				status,
				errorCode,
				authError,
				silentAuthAttempted,
			},
		});
	}, [
		authError,
		country,
		errorCode,
		flowId,
		showSessionExpired,
		showSigningFailed,
		showWrongUser,
		silentAuthAttempted,
		status,
	]);

	useEffect(() => {
		if (
			hasStartedSilentAuth.current ||
			!shouldAttemptSilentAuth({
				country,
				fromApplication,
				attempted: silentAuthAttempted,
				status,
				error: authError,
			})
		) {
			return;
		}
		hasStartedSilentAuth.current = true;
		// The login button is on screen while this runs, and a click would
		// overwrite the state cookie this round-trip depends on.
		isSignInInFlight.current = true;
		setIsSigningIn(true);

		const silentAuthReturnPath = buildSilentAuthUnavailablePath(flowId);
		const maxAge = `Max-Age=${AUTH_ROUND_TRIP_MAX_AGE_SECONDS}`;
		document.cookie = `${SILENT_AUTH_ATTEMPT_COOKIE}=${encodeURIComponent(flowId)}; Path=/; ${maxAge}; SameSite=Lax`;
		document.cookie = `${SILENT_AUTH_RETURN_COOKIE}=${encodeURIComponent(silentAuthReturnPath)}; Path=/; ${maxAge}; SameSite=Lax`;
		document.cookie = `${SILENT_AUTH_FLOW_ID_COOKIE}=${encodeURIComponent(flowId)}; Path=/; ${maxAge}; SameSite=Lax`;
		markGa4LoginStarted();
		trackGa4Event("signing_login_started");

		// On success Keycloak returns straight to the flow root, so the resolver
		// picks the current step without the login screen ever painting.
		void signIn(
			getSigningProviderId(country),
			{
				callbackUrl: buildPublicUrl(
					buildSilentAuthSuccessPath(flowId),
					publicUrl
				),
			},
			getSilentAuthParams(country)
		).catch((error) => {
			// The login screen is already on show, so a silent failure needs no message.
			isSignInInFlight.current = false;
			setIsSigningIn(false);
			clearGa4LoginStarted();
			trackGa4Event("signing_login_failed", {
				error_category: "authentication",
			});
			sendClientLog({
				level: "error",
				event: LogEvent.AuthLoginFailed,
				outcome: "failure",
				message: "Silent sign-in failed to start",
				flowId,
				country,
				context: { provider: getSigningProviderId(country), silent: true },
				error: error as Error,
			});
		});
	}, [
		authError,
		country,
		flowId,
		fromApplication,
		silentAuthAttempted,
		status,
		publicUrl,
	]);

	const clearSilentAuthState = useCallback(() => {
		document.cookie = `${SILENT_AUTH_ATTEMPT_COOKIE}=; Path=/; Max-Age=0`;
		document.cookie = `${SILENT_AUTH_ERROR_COOKIE}=; Path=/; Max-Age=0`;
		document.cookie = `${SILENT_AUTH_FLOW_ID_COOKIE}=; Path=/; Max-Age=0`;
	}, []);

	const navigateToFlow = useCallback(() => {
		router.push(`/${flowId}`);
	}, [flowId, router]);

	if (showWrongUser) {
		return (
			<WrongUser
				onTryAgain={() => {
					clearSilentAuthState();
					void startSignIn({ forceReauth: true });
				}}
				isRetrying={isSigningIn}
			/>
		);
	}

	if (showSigningFailed) {
		return (
			<SigningFailed
				errorCode={errorCode}
				onTryAgain={() => {
					clearSilentAuthState();
					void startSignIn();
				}}
				isRetrying={isSigningIn}
			/>
		);
	}

	if (showSessionExpired) {
		return (
			<SigningSessionExpired
				onTryAgain={() => {
					clearSilentAuthState();
					void startSignIn();
				}}
				onCancelAndSave={navigateToFlow}
				isRetrying={isSigningIn}
			/>
		);
	}

	// A `<ul>` may only hold list items, so the separators are borders, not `<hr>`s.
	const dividedItemClassName = "border-t border-border-base pt-1.5";

	return (
		<StepContainer>
			<Stack gap={3} wrap className="content-center">
				<Stack gap={1}>
					<Heading level={1} textStyle="heading-lg">
						{t("welcomeText.firstLogin.header")}
					</Heading>
					<Text textStyle="body-lg">{t("welcomeText.firstLogin.text1")}</Text>
				</Stack>

				<Card>
					<CardContent className="flex flex-col gap-2 p-3">
						<Heading level={2} textStyle="heading-sm">
							{t("overview.title")}
						</Heading>
						<List.Root>
							<List.Item icon={<Landmark aria-hidden="true" />}>
								{t("overview.steps.step1")}
							</List.Item>
							<List.Item
								icon={<ListCheck aria-hidden="true" />}
								className={dividedItemClassName}
							>
								{t("overview.steps.step2")}
							</List.Item>
							<List.Item
								icon={<BanknoteCheck aria-hidden="true" />}
								className={dividedItemClassName}
							>
								{t("overview.steps.step3")}
							</List.Item>
							<List.Item
								icon={<PencilLine aria-hidden="true" />}
								className={dividedItemClassName}
							>
								{t("overview.steps.step4")}
							</List.Item>
						</List.Root>
					</CardContent>
				</Card>

				<Stack gap={2}>
					{status === LOGIN_STATUS_SESSION_EXPIRED && (
						<Alert severity="warning">{t("sessionExpired")}</Alert>
					)}
					<Button
						onClick={() => void startSignIn()}
						disabled={isSigningIn}
						aria-busy={isSigningIn}
						className="w-full rounded-full flex items-center gap-2"
					>
						{country === "SE" && (
							<BankidSe fill="#fff" width={32} height={32} />
						)}
						{isSigningIn ? t("verifyButtonPending") : t("verifyButton")}
					</Button>
				</Stack>

				{showSimulatorLogin && (
					<Button
						variant="outline"
						onClick={() => void startSignIn({ useSimulator: true })}
						disabled={isSigningIn}
						aria-busy={isSigningIn}
						className="w-full rounded-full"
					>
						{SIMULATOR_BUTTON_LABEL}
					</Button>
				)}
			</Stack>
		</StepContainer>
	);
};
